/**
 * Mixing rules: perceptual volume mapping, hard bus caps and the intensity
 * layer budget (docs/GAME_DESIGN.md §10, docs/AUDIO.md "Mixing").
 *
 * Caps are enforced here AND again in graph.ts when a gain is written to a
 * node, so no settings value (even an out-of-range one) can make a bus louder
 * than its cap. Pure and DOM-free.
 */
import type { AudioSettings } from '../contracts.ts';

/** Hard ceilings for each bus gain, as a fraction of the master gain. */
export const BUS_CAPS = {
  music: 0.35,
  sfx: 0.6,
  ambience: 0.08,
} as const;

export type BusName = keyof typeof BUS_CAPS;

/** The master gain itself never exceeds unity. */
export const MASTER_CAP = 1;

/** Largest boost the tension layer may add on top of the music, in dB. */
export const INTENSITY_MAX_DB = 3;

/**
 * The regular music (melody + pad) is mixed this far below the music bus so
 * that the intensity layer can add up to +3 dB without the bus output ever
 * exceeding the music cap: CONTENT + INTENSITY_LAYER_MAX = 1 (worst case,
 * coherent sum) = +3 dB over CONTENT.
 */
export const MUSIC_CONTENT_GAIN = dbToGain(-INTENSITY_MAX_DB);
export const INTENSITY_LAYER_MAX = 1 - MUSIC_CONTENT_GAIN;

/** Stack height (0–1) below which the tension layer stays silent. */
export const INTENSITY_THRESHOLD = 0.3;

/**
 * Bus ceilings. The caps above limit each bus *gain*; several sounds stacking
 * up (a correct chime + layer break + level-up in one moment) could still sum
 * past full scale inside a bus. So each bus's content first passes a soft
 * clipper whose output never exceeds 1, which makes the caps hold for the
 * signal itself: bus output ≤ 1 × capped gain.
 *
 *  - Below `knee` the curve is the identity — normal material is untouched.
 *    A single sound stays below full scale even with every partial peaking
 *    in phase, and the music below the knee (conservative bounds, unit-tested
 *    in audio-budget.test.ts), so in practice the ceiling only acts when
 *    several effects stack up.
 *  - Above it the level eases towards 1 along a tanh knee (no hard corner).
 *  - `drive` is the input headroom shaped smoothly (sums up to 4× full scale);
 *    `points` is the WaveShaper curve resolution.
 */
export const CEILING = { knee: 0.8, drive: 4, points: 4097 } as const;

/** Soft limiter on the master bus (DynamicsCompressorNode settings). */
export const LIMITER = {
  thresholdDb: -8,
  kneeDb: 8,
  ratio: 12,
  attackS: 0.003,
  releaseS: 0.25,
} as const;

/** Smoothing time constants (s) for parameter changes, so nothing ever clicks. */
export const SMOOTHING = {
  /** Volume sliders and mute. */
  volume: 0.05,
  /** Intensity follows the stack slowly so it never pumps. */
  intensity: 0.6,
} as const;

export interface BusGains {
  master: number;
  music: number;
  sfx: number;
  ambience: number;
}

export function clamp01(x: number): number {
  if (!Number.isFinite(x)) return 0;
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

export function dbToGain(db: number): number {
  return 10 ** (db / 20);
}

export function gainToDb(gain: number): number {
  return 20 * Math.log10(gain);
}

/**
 * Slider position (0–1) → linear gain. Squaring approximates perceived
 * loudness: half-way on the slider is −12 dB, which *sounds* about half as loud.
 * NaN/out-of-range input is clamped.
 */
export function perceptualGain(slider: number): number {
  const x = clamp01(slider);
  return x * x;
}

/** Clamp a bus gain into [0, cap]. The last line of defence before a node. */
export function capBusGain(bus: BusName, gain: number): number {
  return Math.min(BUS_CAPS[bus], clamp01(gain));
}

/**
 * Settings → bus gains. The ambience has no slider of its own; it follows the
 * music slider (it is part of the level's soundscape). Mute zeroes the master
 * only, so unmuting restores the previous balance.
 */
export function computeBusGains(s: AudioSettings): BusGains {
  return {
    master: s.muted ? 0 : Math.min(MASTER_CAP, perceptualGain(s.master)),
    music: capBusGain('music', BUS_CAPS.music * perceptualGain(s.music)),
    sfx: capBusGain('sfx', BUS_CAPS.sfx * perceptualGain(s.sfx)),
    ambience: capBusGain('ambience', BUS_CAPS.ambience * perceptualGain(s.music)),
  };
}

/** Hermite smoothstep of x between edges a < b. */
export function smoothstep(a: number, b: number, x: number): number {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
}

/**
 * Gain of the tension layer for a stack height 0–1: silent below the
 * threshold, easing in to INTENSITY_LAYER_MAX (= +3 dB total) at the top.
 */
export function intensityLayerGain(intensity: number): number {
  return INTENSITY_LAYER_MAX * smoothstep(INTENSITY_THRESHOLD, 1, clamp01(intensity));
}

/** Total music level change (dB) caused by the tension layer, worst case. */
export function intensityBoostDb(intensity: number): number {
  return gainToDb((MUSIC_CONTENT_GAIN + intensityLayerGain(intensity)) / MUSIC_CONTENT_GAIN);
}

/**
 * The bus soft ceiling: identity up to `knee`, then a tanh knee easing
 * towards ±1 (slope 1 at the knee, so the transition is seamless). |result| < 1
 * for every input; NaN maps to silence.
 */
export function softCeiling(x: number, knee: number = CEILING.knee): number {
  if (Number.isNaN(x)) return 0;
  const a = Math.abs(x);
  if (a <= knee) return x;
  return Math.sign(x) * (knee + (1 - knee) * Math.tanh((a - knee) / (1 - knee)));
}

/**
 * WaveShaper curve for softCeiling. A WaveShaper maps inputs in [−1, 1]
 * across the curve, so the graph feeds it through a 1/drive pre-gain and the
 * curve is sampled over x ∈ [−drive, drive].
 */
export function softCeilingCurve(
  points: number = CEILING.points,
  drive: number = CEILING.drive,
  knee: number = CEILING.knee,
): Float32Array<ArrayBuffer> {
  const n = Math.max(2, Math.floor(points));
  const curve = new Float32Array(n);
  for (let i = 0; i < n; i++) curve[i] = softCeiling(drive * (-1 + (2 * i) / (n - 1)), knee);
  return curve;
}
